import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { formatDistance } from 'date-fns'
import { filter, isEmpty, join, map } from 'lodash'
import { ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useIntl } from 'react-intl'

import EditMenu, { EditMenuProps } from '@/components/EditMenu'
import Loading from '@/components/Loading'
import { Size } from '@/components/Modal'
import SharedWith from '@/components/Segments/Builder/SharedWith'
import UserProfile from '@/components/Segments/Builder/UserProfile'
import Button from '@/components/TailwindUI/Button'
import Checkbox from '@/components/TailwindUI/Checkbox'
import StatusIndicator from '@/components/TailwindUI/StatusIndicator'
import cn from '@/utilities/cn'

import { ColumnType, PaginationTableProps } from './interfaces'
import messages from './messages'
import populateAudiences from './populateAudiences'

export type AuthorRow = { modifiedBy: string; modifiedDate: number; enabled: boolean }
export type StatusRow = { variant: string; label: ReactNode }
export type ShowDetailRow = {
  id: string
  detail: ReactNode
}

interface InfiniteScrollTableProps
  extends PaginationTableProps<Column | CheckboxColumn | ShowMoreColumn | EditColumn | AudienceColumn> {
  showDetail?: ShowDetailRow
  onClick?: (row: any) => void
}
interface Column {
  label: ReactNode
  key: string //used by rows to get value
  stackBelow?: Size
  stackParent?: boolean //identifies column as parent of stackBelow columns
  hideBelow?: Size
  sortable?: boolean
  headingClassName?: string
  type?: ColumnType
  rowClassName?: string
}

interface CheckboxColumn extends Column {
  changeSelected: (rowData: any) => void
  selectAllShown: boolean
  selected: string[]
  hasNextPage: boolean
}

interface ShowMoreColumn extends Column {
  onShowMore: (row: any) => void
}
interface EditColumn extends Column {
  editMenuProps: EditMenuProps
}
interface AudienceColumn extends Column {
  defaultAudienceAll: boolean
}

/**
 * Alternative to a paged table, or infinite scroll list. Presents a table with infinite scroll.
 * Initially targeted for use on landing pages, some default column types with formatting/lookups are included.
 *
 * @param tableProps: PaginationTableProps
 * @returns ReactNode
 */
export default function InfiniteScrollTable({
  className,
  columns,
  showOnEmpty = false,
  rows,
  hasNextPage = false,
  loading = false,
  onLoadMore,
  disabled = false,
  sortBy,
  setSortBy,
  isFiltered = false,
  showDetail,
  onClick
}: InfiniteScrollTableProps) {
  const intl = useIntl()
  const showTable = (showOnEmpty && rows.length === 0) || rows.length > 0
  const stackBelowColumns = filter(columns, column => !!column?.stackBelow)

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
    disabled
  })

  return loading ? (
    <div className="absolute flex h-full w-full items-center justify-center bg-zinc-300 bg-opacity-80">
      <Loading isLoading={true} loadingMessage="Loading..." />
    </div>
  ) : showTable ? (
    <div className={cn(className)}>
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-zinc-300">
            {map(columns, column => {
              return (
                <th
                  scope="col"
                  className={cn(
                    {
                      hidden: column?.hideBelow || column?.stackBelow,
                      'xs:table-cell': column?.hideBelow === 'xs' || column?.stackBelow === 'xs',
                      'sm:table-cell': column?.hideBelow === 'sm' || column?.stackBelow === 'sm',
                      'md:table-cell': column?.hideBelow === 'md' || column?.stackBelow === 'md',
                      'lg:table-cell': column?.hideBelow === 'lg' || column?.stackBelow === 'lg',
                      'xl:table-cell': column?.hideBelow === 'xl' || column?.stackBelow === 'xl'
                    },
                    'py-3.5 pr-3 pl-2 text-left text-base font-semibold text-zinc-900',
                    column?.headingClassName
                  )}
                >
                  {column.sortable ? (
                    <Button
                      color="transparent"
                      className={cn('-ml-3 flex w-full justify-start text-base font-semibold')}
                      onClick={
                        setSortBy
                          ? () =>
                              setSortBy({
                                value: column.key,
                                direction: sortBy?.direction === 'desc' ? 'asc' : 'desc'
                              })
                          : undefined
                      }
                    >
                      {column.label}
                      <DynamicChevron
                        direction={sortBy?.value === column.key ? sortBy.direction : 'desc'}
                        isSelected={sortBy?.value === column.key}
                        className="ml-2"
                      />
                    </Button>
                  ) : 'selected' in column ? (
                    <Checkbox
                      checked={!isFiltered && !hasNextPage && column.selected.length === rows.length}
                      onChange={() => {
                        column.changeSelected(column.selected.length === rows.length ? [] : rows)
                      }}
                    />
                  ) : (
                    column.label
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {map(rows, (row, index) => {
            return (
              <>
                <tr
                  key={row.id || index}
                  className={cn('border-t border-zinc-200 hover:bg-zinc-100', {
                    'bg-zinc-100': showDetail && (showDetail?.id === row?.id || showDetail?.id === row?.index)
                  })}
                  tabIndex={0}
                  aria-selected={showDetail && (showDetail?.id === row?.id || showDetail?.id === row?.index)}
                  onClick={() => onClick && onClick(row)}
                  role={onClick ? 'button' : 'row'}
                >
                  {map(columns, column => (
                    <td
                      className={cn(
                        'px-3 py-4 text-sm text-zinc-500',
                        {
                          hidden: column?.hideBelow || column?.stackBelow,
                          'xs:table-cell': column?.hideBelow === 'xs' || column?.stackBelow === 'xs',
                          'sm:table-cell': column?.hideBelow === 'sm' || column?.stackBelow === 'sm',
                          'md:table-cell': column?.hideBelow === 'md' || column?.stackBelow === 'md',
                          'lg:table-cell': column?.hideBelow === 'lg' || column?.stackBelow === 'lg',
                          'xl:table-cell': column?.hideBelow === 'xl' || column?.stackBelow === 'xl',
                          'text-base font-medium text-zinc-900 sm:w-auto sm:max-w-none': column?.stackParent,
                          'hover:cursor-pointer': !!onClick
                        },
                        column?.rowClassName
                      )}
                      key={`${column.key}-${row.id}`}
                    >
                      {(() => {
                        if ('selected' in column) {
                          return (
                            <Checkbox
                              checked={row[column.key] || column.selected.includes(row.id)}
                              onChange={() => column.changeSelected && column.changeSelected(row)}
                            />
                          )
                        }
                        if ('onShowMore' in column) {
                          return (
                            <Button color="transparent" onClick={() => column.onShowMore(row)}>
                              <DynamicChevron direction="desc" isSelected={row[column.key]} className="stroke-[3px]" />
                            </Button>
                          )
                        }
                        if ('editMenuProps' in column) {
                          return <EditMenu {...column.editMenuProps} rowData={row} />
                        }

                        const isUuid = /^[\d,a-z-]{36}$/
                        switch (column.type) {
                          case ColumnType.Status: {
                            return <StatusIndicator {...row[column.key]} />
                          }
                          case ColumnType.Author: {
                            const { modifiedBy, modifiedDate, enabled = true } = row[column.key] || row
                            let formattedDate = ''
                            try {
                              formattedDate = formatDistance(new Date(modifiedDate), new Date(), { addSuffix: true })
                            } catch {}
                            return (
                              <div className="flex text-center">
                                {isUuid.test(modifiedBy) ? (
                                  <UserProfile userId={modifiedBy} enabled={enabled} />
                                ) : (
                                  <div className={'flex items-center space-x-2 text-sm'}>
                                    <p className="fade-in text-zinc-700 line-clamp-1">{modifiedBy}</p>
                                  </div>
                                )}
                                {!!modifiedDate && !isEmpty(formattedDate) && (
                                  <div className="fade-in ml-2 text-xs leading-7 text-zinc-500 line-clamp-1">
                                    {intl.formatMessage(messages.updated)} {formattedDate}
                                  </div>
                                )}
                              </div>
                            )
                          }
                          case ColumnType.Audience: {
                            return (
                              <div className="items-left column text-sm">
                                {'defaultAudienceAll' in column
                                  ? populateAudiences(row[column.key], column?.defaultAudienceAll)
                                  : populateAudiences(row[column.key])}
                              </div>
                            )
                          }
                          case ColumnType.Owners: {
                            return <SharedWith sharedWith={row[column.key]} isLoading={false} className="w-fit" />
                          }
                          case ColumnType.EditMenu: {
                            return <EditMenu menuItems={row[column.key]} rowData={row} />
                          }
                          default: {
                            return Array.isArray(row[column.key])
                              ? (join(row[column.key], ', ') as string)
                              : row[column.key]
                          }
                        }
                      })()}
                      {stackBelowColumns && column?.stackParent && (
                        <dl>
                          {map(stackBelowColumns, stackBelowColumn => {
                            return (
                              <div
                                className={cn('font-normal', `${stackBelowColumn.stackBelow}:hidden visible`, {
                                  'xs:hidden': stackBelowColumn?.stackBelow === 'xs',
                                  'sm:hidden': stackBelowColumn?.stackBelow === 'sm',
                                  'md:hidden': stackBelowColumn?.stackBelow === 'md',
                                  'lg:hidden': stackBelowColumn?.stackBelow === 'lg',
                                  'xl:hidden': stackBelowColumn?.stackBelow === 'xl'
                                })}
                              >
                                <dt className="sr-only">{stackBelowColumn.label}</dt>
                                <dd className="mt-1 truncate text-xs text-zinc-500">{row[stackBelowColumn.key]}</dd>
                              </div>
                            )
                          })}
                        </dl>
                      )}
                    </td>
                  ))}
                </tr>
                {showDetail && (showDetail?.id === row?.id || showDetail?.id === row?.index) && (
                  <tr key={`${row.id || index}-detail`} className="collapse bg-zinc-100 lg:visible">
                    <th scope="colgroup" colSpan={columns.length}>
                      {showDetail?.detail}
                    </th>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </table>

      {hasNextPage && <div className="flex w-full border" ref={sentryRef} />}
    </div>
  ) : (
    <div className="mt-4 flex items-center justify-center text-lg italic text-zinc-700">
      {intl.formatMessage(messages.NoSearchResult)}
    </div>
  )
}

const DynamicChevron = ({
  direction = 'desc',
  isSelected = false,
  className
}: {
  direction?: string
  isSelected?: boolean
  className?: string
}) => {
  const selected = {
    'rounded text-zinc-900 stroke-[3px]': isSelected,
    'text-zinc-400': !isSelected
  }
  return direction === 'desc' ? (
    <ChevronDownIcon className={cn('mt-1 h-4 w-4', selected, className)} />
  ) : (
    <ChevronUpIcon className={cn('mt-1 h-4 w-4', selected, className)} />
  )
}

// Possible future additions: sticky header
